import { Box, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components/datagrid/Custom';
import { useEffect, useState } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { getThrottlingGroups } from 'redux/slices/throttlingGroup';
import { RootState, useSelector } from 'redux/store';
import { ThrottlingGroup } from '../../../@types/throttlingGroup';
import AddThrottlingGroupDialog from './AddThrottlingGroupDialog';
import { Button } from '@nps.today/component-library';
import ThrottlingGroupCampaignsDialog from './ThrottlingGroupCampaignsDialog';
import { DataGridKey } from 'redux/slices/datagrid';
import { EditListItem } from 'components/datagrid/listItems/EditListItem';
import { useTranslator } from 'translation/useTranslator';

const ThrottlingGroupsList = () => {
  const translator = useTranslator();
  const { throttlingGroups, isLoading: isThrottlingGroupLoading } = useSelector(
    (state: RootState) => state.throttlingGroup
  );
  const isLoading = isThrottlingGroupLoading.getThrottlingGroups;
  const customDispatch = useCustomDispatch();
  const [throttlingGroupToEdit, setThrottlingGroupToEdit] = useState<ThrottlingGroup | undefined>();

  useEffect(() => {
    customDispatch({ action: getThrottlingGroups, disableSuccessMessage: true });
  }, [customDispatch]);

  const [selectedThrottlingGroup, setSelectedThrottlingGroup] = useState<
    ThrottlingGroup | undefined
  >(undefined);

  const columns: GridColDef<ThrottlingGroup>[] = [
    {
      field: 'id',
      headerName: 'id',
      type: 'number',
      valueGetter: (_value, row) => row.id,
      renderCell: (params: GridRenderCellParams<ThrottlingGroup>) => params.row.id
    },
    {
      field: 'name',
      headerName: translator.name(),
      flex: 1
    },
    {
      field: 'campaigns',
      headerName: translator.campaigns(),
      flex: 1,
      filterable: false,
      sortable: false,
      valueGetter: (_value, row) => row.campaignIds?.length ?? 0,
      renderCell: (params: GridRenderCellParams<ThrottlingGroup>) => {
        const campaignIds = params.row.campaignIds ?? [];
        if (campaignIds.length <= 0) return <></>;

        const throttlingGroupId = params.row.id;
        const throttlingGroup = throttlingGroups.find(
          (throttlingGroup) => throttlingGroup.id === throttlingGroupId
        );
        return (
          <Button
            dataTestId="throttling-group-list-campaigns"
            onClick={() => setSelectedThrottlingGroup(throttlingGroup)}
          >
            {translator.xCampaigns(`${campaignIds.length}`)}
          </Button>
        );
      }
    },
    {
      field: 'edit',
      headerName: translator.edit(),
      width: 60,
      hideable: false,
      editable: false,
      align: 'center',
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      disableExport: true,
      renderCell: (cellValues: GridRenderCellParams<ThrottlingGroup>) => {
        const throttlingGroup = cellValues.row;
        return (
          <>
            {/**
             * If the id is null, the group is the default group, and is not editable. '' tellts the dialog that the group is default
             */}
            <EditListItem onClick={() => setThrottlingGroupToEdit(throttlingGroup)} />
          </>
        );
      }
    }
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        <Typography variant="body2">{translator.thisPageShowsAllThrottlingGroups()}</Typography>
        <Typography variant="body2">{translator.throttlingFunctionalityDescription()}</Typography>
        <Typography variant="body2">
          {translator.throttlingRulesOnlyApplyToCampaignsInSameGroup()}
        </Typography>
      </div>

      <Box p={1} />
      <CustomDataGrid
        getRowId={(row) => row.id ?? row.name}
        dataGridKey={DataGridKey.ThrottlingGroups}
        autoHeight
        loading={isLoading}
        disableRowSelectionOnClick
        rows={throttlingGroups}
        columns={columns}
        pagination
        paginationModel={{ page: 0, pageSize: 25 }}
        pageSizeOptions={[25]}
      />
      <AddThrottlingGroupDialog
        isOpen={Boolean(throttlingGroupToEdit)}
        close={() => setThrottlingGroupToEdit(undefined)}
        fetchThrottlingGroupsOnSuccess
        throttlingGroupToEdit={throttlingGroupToEdit}
      />
      <ThrottlingGroupCampaignsDialog
        dataGridKey={DataGridKey.ThrottlingGroupCampaigns}
        throttlingGroup={selectedThrottlingGroup}
        resetThrottlingGroup={() => setSelectedThrottlingGroup(undefined)}
      />
    </>
  );
};

export default ThrottlingGroupsList;
